import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { FileText, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin panel sub-tab: Recetas (prescription renewal) metrics.
 * Aggregates volumes + resolution times + escalations to clinical consult.
 * Data source: GET /api/admin/recetas/metrics
 */
const RecetasMetricsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await fetch(`${API_URL}/api/admin/recetas/metrics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          setMetrics(await res.json());
        } else {
          toast.error("No pudimos cargar las métricas de recetas");
        }
      } catch (e) {
        console.error(e);
        toast.error("Error de conexión");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <p className="text-sm text-slate-500">Cargando métricas…</p>;
  }
  if (!metrics) return null;

  const stats = [
    {
      label: "Total recetas",
      value: metrics.total ?? 0,
      icon: FileText,
      color: "text-fuchsia-600 bg-fuchsia-50",
      testid: "recetas-total",
    },
    {
      label: "Resueltas",
      value: metrics.resolved ?? 0,
      icon: CheckCircle2,
      color: "text-emerald-600 bg-emerald-50",
      testid: "recetas-resolved",
    },
    {
      label: "Pendientes",
      value: metrics.pending ?? 0,
      icon: Clock,
      color: "text-amber-600 bg-amber-50",
      testid: "recetas-pending",
    },
    {
      label: "Derivadas a consulta",
      value: metrics.requires_clinical_consult ?? 0,
      icon: AlertCircle,
      color: "text-blue-600 bg-blue-50",
      testid: "recetas-requires-consult",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className="p-5">
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${s.color} mb-3`}>
                <Icon className="h-5 w-5" />
              </div>
              <p className="text-2xl font-semibold text-slate-900" data-testid={s.testid}>
                {s.value}
              </p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </Card>
          );
        })}
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="font-semibold text-slate-900">Tiempo promedio de resolución</h3>
          <span className="text-2xl font-bold text-slate-900" data-testid="recetas-avg-hours">
            {metrics.avg_resolution_hours != null ? `${metrics.avg_resolution_hours} h` : "—"}
          </span>
        </div>
        <p className="text-sm text-slate-500">
          Tiempo desde que el paciente envía la solicitud hasta que el médico marca la receta como
          resuelta (confeccionada en SIDCA u otro sistema institucional). El default sugerido es de 48 horas hábiles.
        </p>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold text-slate-900 mb-3">Distribución por estado</h3>
        {(metrics.by_status || []).length === 0 ? (
          <p className="text-sm text-slate-500">Sin datos.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(metrics.by_status || []).map((row) => (
              <li key={row.status} className="py-2 flex items-center justify-between text-sm">
                <span className="text-slate-700">{row.status}</span>
                <span className="font-medium text-slate-900">{row.count}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default RecetasMetricsPanel;
